// components/models/QwenImage3ControlPanel.tsx
import React, { useCallback, memo } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import { QwenImage3Params, QwenImage3Resolution } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

export const getDefaultQwenImage3Params = (): QwenImage3Params => ({
  prompt: '',
  seed: -1,
  negative_prompt: '',
  resolution: '2K',
});

const RESOLUTION_OPTIONS = ['2K', '1K'];
const SELECT_STYLE = { width: '100%' };

const QwenImage3ControlPanel: React.FC = () => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as QwenImage3Params | undefined
  );

  const updateParam = useCallback((name: string, value: string) => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;

    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) return prevModelParams;
      return { ...prevModelParams, [diffusionModel]: { ...currentParams, [name]: value } };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value_length: value.length,
      model_name: diffusionModel,
      source_component: 'QwenImage3ControlPanel',
    });
  }, [kernel]);

  const handleParamChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    updateParam(e.target.name, e.target.value);
  }, [updateParam]);

  const handleResolutionChange = useCallback((val: string) => {
    updateParam('resolution', val as QwenImage3Resolution);
  }, [updateParam]);

  if (!params) return null;

  return (
    <>
      <div>
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe how to edit or generate the image..."
          description='Put every string that must appear in the image in "double quotes".'
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mt-4">
        <AutoGrowingTextarea
          label="Negative Prompt"
          placeholder="Describe what to avoid..."
          description="Optional elements/styles to exclude."
          name="negative_prompt"
          value={params.negative_prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={240}
          labelClassName="block text-sm font-medium text-white mb-1"
        />
      </div>

      <div className="mt-4">
        <Select
          id="resolution"
          label="Resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={handleResolutionChange}
          style={SELECT_STYLE}
        />
      </div>
    </>
  );
};

export default memo(QwenImage3ControlPanel);
